import { forwardRef, useEffect, useImperativeHandle, useMemo } from 'react';
import {
  FormHelperText,
  Stack,
  Typography,
  FormControl,
  InputLabel,
  Select,
  TextField,
  MenuItem,
} from '@mui/material';
import useForm from '../../../pages/AdminContentMgt/Components/useForm';
import { useQuery } from '@tanstack/react-query';
import { GetProducts } from '../../../api/proposals';
import { versionExtract } from '../../Utilities/HelperMethods';
import ProducerIDsDropdown from '../../Global/ProducerIDsDropdown/ProducerIDsDropdown';

const ProposalInfo = forwardRef((props, ref) => {
  const { versions, client, versionList, isEditing, oldVersionList = [] } = props;

  const productQuery = useQuery({
    queryKey: ['GetProducts'],
    queryFn: async () => await GetProducts()(),
  });

  const validate = (fieldValues = values) => {
    const isDuplicate =
      versionList?.length > 0
        ? versionList.find((item) =>
            item?.versionName
              ?.toLowerCase()
              ?.trim()
              .includes(fieldValues?.versionName?.trim().toLowerCase())
          ) === undefined
        : true;
    let temp = { ...errors };
    if ('versionName' in fieldValues)
      temp.versionName = fieldValues.versionName ? '' : 'Version Name is required.';

    if (isEditing && !isDuplicate) temp.versionName = 'Duplicate version name.';

    if ('ProducerID' in fieldValues) {
      const isValid =
        fieldValues.ProducerID &&
        values.ProducerID !== 'undefined-undefined' &&
        values.ProducerID.toUpperCase() !== 'HO';

      temp.ProducerID = isValid ? '' : 'Producer ID is required.';
    }

    setErrors({
      ...temp,
    });

    if (fieldValues == values) return Object.values(temp).every((x) => x == '');
  };

  useEffect(() => {
    values.versionName =
      oldVersionList?.length === 0
        ? versions?.[0]?.versionName
        : oldVersionList?.find((version) => version?.versionName === versions?.[0]?.versionName) ===
            undefined
          ? versions?.[0]?.versionName
          : '';
    values.version = versions?.[0]?.id || '';
    values.product = versions?.length
      ? `${versions?.[0]?.productID}-${versions?.[0]?.illustrationData?.product}`
      : '';
    values.ProducerID = versions?.length ? `${versions?.[0]?.illustrationData?.ProducerID}` : '';
  }, [versions, isEditing]);

  const { values, errors, setErrors, handleInputChange, handleIntChange, resetForm } = useForm(
    {
      versionName: '',
      version: '',
      product: '',
      ProducerID: '',
    },
    true,
    validate
  );

  const handleVersionChange = (e) => {
    const version = oldVersionList?.find((version) => version.id === +e.target.value);
    if (version) {
      handleInputChange({
        target: {
          name: 'product',
          value: `${versions?.[0]?.productID}-${versions?.[0]?.illustrationData?.product}`,
        },
      });
      handleInputChange(e);
    }
  };

  const getClientInfo = () => {
    if (validate()) {
      const { data: productOption } = productQuery;
      const foundVersion = oldVersionList?.find((version) => +version.id === +values.version);
      let createdVersions = foundVersion ? [foundVersion] : [];
      const selectedProduct = productOption?.find(
        (proId) => +proId.id === +values.product.split('-')[0]
      );
      let updatedVersions = [];
      if (createdVersions.length > 0 && values.version !== '') {
        const currentVersion = createdVersions.find((version) => +version.id === +values.version);
        updatedVersions.push({
          ...currentVersion,
          versionName: values.versionName,
          product: selectedProduct?.value,
          illustrationData: {
            ...client,
            ...versionExtract(currentVersion?.illustrationData),
            ProducerID: values.ProducerID,
          },
          productID: selectedProduct?.id,
        });
      } else {
        updatedVersions.push({
          clientID: client?.id,
          versionName: values.versionName,
          product: selectedProduct?.value,
          illustrationData: {
            ...client,
            product: selectedProduct?.value,
            ...versions?.[0]?.illustrationData,
            ProducerID: values.ProducerID,
          },
          productID: selectedProduct?.id,
        });
      }
      resetForm();
      return {
        createdVersions: updatedVersions,
        product: selectedProduct?.id,
        productName: values.product.split('-')[1],
      };
    }
  };

  const { versionName, version, product, ProducerID } = values;
  const memoizValues = useMemo(
    () => ({ versionName, version, product, ProducerID }),
    [versionName, version, product, ProducerID]
  );

  const { versionName: vnErr, version: vErr, product: prErr } = errors;
  const memoizErrors = useMemo(() => ({ vnErr, vErr, prErr }), [vnErr, vErr, prErr]);

  const { id, text, value } = productQuery?.data || {};
  const memoizproductOptions = useMemo(() => ({ id, text, value }), [id, text, value]);

  useImperativeHandle(ref, () => {
    return {
      getClientInfo: () => getClientInfo(),
    };
  }, [memoizValues, memoizErrors, memoizproductOptions]);

  const setOverrideProducer = (producerID) => {
    handleInputChange({
      target: {
        name: 'ProducerID',
        value: producerID,
      },
    });
  };

  return (
    <div>
      {/*Proposal information*/}
      <Stack>
        <Typography mb={1} variant="h6">
          Proposal Information
        </Typography>
        {oldVersionList?.length > 0 && (
          <Stack mb={2} spacing={3}>
            <FormControl fullWidth>
              <TextField
                label="Version"
                name="version"
                variant="standard"
                select={true}
                value={values.version}
                onChange={handleVersionChange}
                error={errors.version !== undefined && errors.version !== ''}
              >
                <MenuItem value="">Select version</MenuItem>
                {oldVersionList?.length > 0 &&
                  oldVersionList.map((item, index) => {
                    const { versionName, id } = item;
                    return (
                      <MenuItem value={id} key={`${versionName}-${index}`}>
                        {versionName}
                      </MenuItem>
                    );
                  })}
              </TextField>
              <FormHelperText className="color-error">{errors.version}</FormHelperText>
            </FormControl>
          </Stack>
        )}
        <Stack mb={3}>
          <TextField
            required
            name="versionName"
            variant="standard"
            label="Version Name"
            value={values.versionName}
            onChange={handleInputChange}
            error={errors.versionName !== undefined && errors.versionName !== ''}
            helperText={errors.versionName}
          />
        </Stack>
        <Stack mb={2}>
          <FormControl fullWidth>
            <TextField
              name="product"
              select={true}
              variant="standard"
              label="Product"
              value={values.product}
              onChange={handleInputChange}
              error={errors.product !== undefined && errors.product !== ''}
              SelectProps={{
                MenuProps: {
                  MenuListProps: {
                    sx: {
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      gap: 1,
                    },
                  },
                },
              }}
            >
              <MenuItem value="">Select Product</MenuItem>
              {productQuery?.data?.map((item, index) => (
                <MenuItem value={`${item.id}-${item.value}`} key={index}>
                  {item.text}
                </MenuItem>
              ))}
            </TextField>
            <FormHelperText className="color-error">{errors.product}</FormHelperText>
          </FormControl>
        </Stack>
        <Stack mb={2}>
          <ProducerIDsDropdown
            setInititalProducerID={(id) => {
              setOverrideProducer(id);
            }}
            defaultValue={values?.ProducerID}
            setProducerID={(id) => {
              setOverrideProducer(id);
            }}
            error={errors.ProducerID !== undefined && errors.ProducerID !== ''}
            helperText={errors.ProducerID}
            variant="standard"
          />
        </Stack>
      </Stack>
    </div>
  );
});

export default ProposalInfo;
